/**
 * SEO Route Policy - Single source of truth for route decisions
 *
 * Vercel SPA Behavior: Default Vercel routing is sufficient.
 * - Valid routes: Serve prerendered HTML or SPA fallback
 * - Invalid routes: SPA fallback renders NotFound with noindex
 * - No vercel.json needed; NotFound component handles SEO-safe 404s
 *
 * Published Content Sources:
 * - Blog posts: Supabase 'blog_posts' table with status = 'published'
 *   - Sitemap generation queries Supabase directly for published posts
 * - Projects: Supabase 'projects' table with is_published = true
 *   - Fallback: src/data/worksData.ts when Supabase unavailable
 *   - Sitemap generation queries Supabase directly for published projects
 */

export const CANONICAL_ORIGIN = "https://www.afidev.me";

export const STATIC_PUBLIC_ROUTES = ["/", "/project", "/blog"] as const;
export const STATIC_INTERNAL_ROUTES = [
  "/admin",
  "/login",
  "/test/error",
] as const;
export const DYNAMIC_ROUTE_FAMILIES = [
  "/project/:slug",
  "/blog/:slug",
] as const;

export type StaticPublicRoute = (typeof STATIC_PUBLIC_ROUTES)[number];
export type StaticInternalRoute = (typeof STATIC_INTERNAL_ROUTES)[number];
export type DynamicRouteFamily = (typeof DYNAMIC_ROUTE_FAMILIES)[number];

export interface SitemapRouteMetadata {
  changefreq: "weekly" | "monthly";
  priority: string;
}

export interface StaticRoutePolicy {
  path: StaticPublicRoute | StaticInternalRoute;
  indexable: boolean;
  includeInSitemap: boolean;
  prerenderEligible: boolean;
  sitemap?: SitemapRouteMetadata;
}

export interface DynamicRouteFamilyPolicy {
  pattern: DynamicRouteFamily;
  familyExists: true;
  indexability: "per-instance";
  sitemapInclusion: "published-and-valid-slug";
  prerenderEligibility: "published-and-valid-slug";
  sitemap: SitemapRouteMetadata;
  invalidBehavior: {
    notFound: true;
    noindex: true;
    includeInSitemap: false;
    prerenderEligible: false;
  };
}

export interface DynamicRouteInstanceInput {
  slug?: string | null;
  published: boolean;
}

export interface DynamicRouteInstancePolicy {
  pattern: DynamicRouteFamily;
  familyExists: true;
  slug: string | null;
  validSlug: boolean;
  published: boolean;
  notFound: boolean;
  noindex: boolean;
  indexable: boolean;
  includeInSitemap: boolean;
  prerenderEligible: boolean;
}

const validSlugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const staticRoutePolicies: Record<
  StaticPublicRoute | StaticInternalRoute,
  StaticRoutePolicy
> = {
  "/": {
    path: "/",
    indexable: true,
    includeInSitemap: true,
    prerenderEligible: true,
    sitemap: { priority: "1.0", changefreq: "weekly" },
  },
  "/project": {
    path: "/project",
    indexable: true,
    includeInSitemap: true,
    prerenderEligible: true,
    sitemap: { priority: "0.9", changefreq: "weekly" },
  },
  "/blog": {
    path: "/blog",
    indexable: true,
    includeInSitemap: true,
    prerenderEligible: true,
    sitemap: { priority: "0.8", changefreq: "weekly" },
  },
  "/admin": {
    path: "/admin",
    indexable: false,
    includeInSitemap: false,
    prerenderEligible: false,
  },
  "/login": {
    path: "/login",
    indexable: false,
    includeInSitemap: false,
    prerenderEligible: false,
  },
  "/test/error": {
    path: "/test/error",
    indexable: false,
    includeInSitemap: false,
    prerenderEligible: false,
  },
};

const dynamicRouteFamilyPolicies: Record<
  DynamicRouteFamily,
  DynamicRouteFamilyPolicy
> = {
  "/project/:slug": {
    pattern: "/project/:slug",
    familyExists: true,
    indexability: "per-instance",
    sitemapInclusion: "published-and-valid-slug",
    prerenderEligibility: "published-and-valid-slug",
    sitemap: { priority: "0.8", changefreq: "monthly" },
    invalidBehavior: {
      notFound: true,
      noindex: true,
      includeInSitemap: false,
      prerenderEligible: false,
    },
  },
  "/blog/:slug": {
    pattern: "/blog/:slug",
    familyExists: true,
    indexability: "per-instance",
    sitemapInclusion: "published-and-valid-slug",
    prerenderEligibility: "published-and-valid-slug",
    sitemap: { priority: "0.7", changefreq: "monthly" },
    invalidBehavior: {
      notFound: true,
      noindex: true,
      includeInSitemap: false,
      prerenderEligible: false,
    },
  },
};

function normalizeSlug(slug?: string | null) {
  const trimmedSlug = slug?.trim();
  return trimmedSlug ? trimmedSlug : null;
}

export function isValidSlug(slug?: string | null) {
  const normalizedSlug = normalizeSlug(slug);
  return normalizedSlug !== null && validSlugPattern.test(normalizedSlug);
}

export function getStaticRoutePolicy(
  path: StaticPublicRoute | StaticInternalRoute,
) {
  return staticRoutePolicies[path];
}

export function getDynamicRouteFamilyPolicy(pattern: DynamicRouteFamily) {
  return dynamicRouteFamilyPolicies[pattern];
}

export function evaluateDynamicRouteInstance(
  pattern: DynamicRouteFamily,
  input: DynamicRouteInstanceInput,
): DynamicRouteInstancePolicy {
  const slug = normalizeSlug(input.slug);
  const validSlug = isValidSlug(slug);
  const indexable = validSlug && input.published;

  return {
    pattern,
    familyExists: true,
    slug,
    validSlug,
    published: input.published,
    notFound: !validSlug,
    noindex: !indexable,
    indexable,
    includeInSitemap: indexable,
    prerenderEligible: indexable,
  };
}

export function evaluateProjectRoute(input: DynamicRouteInstanceInput) {
  return evaluateDynamicRouteInstance("/project/:slug", input);
}

export function evaluateBlogRoute(input: DynamicRouteInstanceInput) {
  return evaluateDynamicRouteInstance("/blog/:slug", input);
}

export const routePolicy = {
  canonicalOrigin: CANONICAL_ORIGIN,
  staticPublicRoutes: STATIC_PUBLIC_ROUTES,
  staticInternalRoutes: STATIC_INTERNAL_ROUTES,
  dynamicFamilies: DYNAMIC_ROUTE_FAMILIES,
  getStaticRoutePolicy,
  getDynamicRouteFamilyPolicy,
  evaluateDynamicRouteInstance,
  evaluateProjectRoute,
  evaluateBlogRoute,
  isValidSlug,
} as const;
